// 경기 수 점검 카드 — 운영진 전용 접힘 카드.
//
// 이벤트 상세에서 참가자 섹션 끝에 노출. 라운드별 대진이 생성된 뒤(스케줄
// 있음)에만 보이며, draft/ready 단계에선 숨김. 운영진이 현장에서 누가 적게
// 쳤는지 한눈에 보고 코트 투입/스왑 판단을 빠르게 하라는 용도.
//
// 데이터는 이미 로드된 matches/participants에서 계산하므로 추가 fetch 없음.
// swap/스케줄 재생성 후 부모가 rerender하면 자동 재집계.
//
// 교류전인 경우 호스트팀(team_order=1)만 집계. 게스트팀은 우리 클럽 운영
// 의사결정과 무관하므로 미렌더.
//
// Parked 설계 부활: docs/parked_features.md §1.

import { useState } from "preact/hooks";
import { matchPlayerName } from "../matches";
import { Icon } from "../ui/components/Icon";
import { mountPreact } from "../ui/preact-mount";

interface ParticipantLike {
  event_participant_id: string;
}

interface MatchLike {
  match_type?: string;
  round_number?: number | null;
  player_ids?: string[];
  result_status?: string;
}

interface TeamLike {
  team_order?: number;
  members?: Array<{ event_participant_id: string }>;
}

interface RowStat {
  pid: string;
  name: string;
  total: number;
  men: number;
  women: number;
  mixed: number;
  exceptional: number;
  rounds: number[];
}

export interface MatchCountCheckParams {
  ev: any;
  matches: MatchLike[];
  participants: ParticipantLike[];
  teams: TeamLike[];
}

function computeStats(
  matches: MatchLike[],
  participants: ParticipantLike[],
  teams: TeamLike[],
  isFriendly: boolean,
): RowStat[] {
  // 교류전이면 호스트팀(team_order=1) 멤버만 집계
  let eligiblePids: Set<string> | null = null;
  if (isFriendly) {
    const hostTeam = teams.find((t) => t.team_order === 1);
    if (hostTeam) {
      eligiblePids = new Set((hostTeam.members || []).map((m) => m.event_participant_id));
    }
  }

  const statsMap = new Map<string, RowStat>();
  for (const p of participants) {
    if (eligiblePids && !eligiblePids.has(p.event_participant_id)) continue;
    statsMap.set(p.event_participant_id, {
      pid: p.event_participant_id,
      name: matchPlayerName(p),
      total: 0,
      men: 0,
      women: 0,
      mixed: 0,
      exceptional: 0,
      rounds: [],
    });
  }

  for (const m of matches) {
    if (m.result_status === "skipped") continue;
    const pids = m.player_ids || [];
    const type = m.match_type || "";
    const round = m.round_number;
    for (const pid of pids) {
      const s = statsMap.get(pid);
      if (!s) continue;
      s.total += 1;
      if (type === "men_doubles") s.men += 1;
      else if (type === "women_doubles") s.women += 1;
      else if (type === "mixed_doubles" || type === "open_doubles") s.mixed += 1;
      else s.exceptional += 1;
      if (round != null && !s.rounds.includes(round)) s.rounds.push(round);
    }
  }

  return [...statsMap.values()].sort((a, b) => {
    if (a.total !== b.total) return a.total - b.total; // 적게 친 사람부터 — 다음 투입 후보가 위로
    return a.name.localeCompare(b.name);
  });
}

function MatchCountCheck(params: MatchCountCheckParams) {
  const { ev, matches, participants, teams } = params;
  const [open, setOpen] = useState(false);

  if (!matches || matches.length === 0) return null;

  const isFriendly = !!ev.is_friendly_match;
  const stats = computeStats(matches, participants, teams, isFriendly);
  if (stats.length === 0) return null;

  const totals = stats.map((s) => s.total);
  const min = Math.min(...totals);
  const max = Math.max(...totals);

  // 매치 단위 유형 합계 (each match counted once)
  const typeMatches = { men: 0, women: 0, mixed: 0, exceptional: 0 };
  for (const m of matches) {
    if (m.result_status === "skipped") continue;
    if (m.match_type === "men_doubles") typeMatches.men += 1;
    else if (m.match_type === "women_doubles") typeMatches.women += 1;
    else if (m.match_type === "mixed_doubles" || m.match_type === "open_doubles") typeMatches.mixed += 1;
    else typeMatches.exceptional += 1;
  }

  // 잡복 컬럼은 실제 잡복 매치가 있을 때만 노출 — 보통 정기모임은 잡복이
  // 거의 안 생겨서 빈 컬럼이 자리만 차지하는 걸 피한다.
  const hasExceptional = typeMatches.exceptional > 0;
  const tableCls = "match-count-table" + (hasExceptional ? " match-count-table--with-mix" : "");

  return (
    <div class="section section--panel">
      <div
        class="section__title section__title--toggle"
        onClick={() => setOpen((v) => !v)}
      >
        <h3>경기 수 점검</h3>
        <span class={"section__chevron" + (open ? " section__chevron--open" : "")}>
          <Icon name="chevron" size={14} />
        </span>
      </div>
      <div hidden={!open}>
        <p class="muted small" style="margin:0 0 8px">
          최소 {min}회 · 최대 {max}회 · 총 {stats.length}명
        </p>
        <div class={tableCls}>
          <div class="match-count-row match-count-row--head">
            <span class="match-count-row__name">이름</span>
            <span class="match-count-row__num">총</span>
            <span class="match-count-row__num">남복</span>
            <span class="match-count-row__num">혼복</span>
            <span class="match-count-row__num">여복</span>
            {hasExceptional && <span class="match-count-row__num">잡복</span>}
            <span class="match-count-row__rounds">라운드</span>
          </div>
          {stats.map((s) => (
            <div key={s.pid} class="match-count-row">
              <span class="match-count-row__name">{s.name}</span>
              <span class="match-count-row__num match-count-row__num--bold">{s.total}</span>
              <span class="match-count-row__num">{s.men || "-"}</span>
              <span class="match-count-row__num">{s.mixed || "-"}</span>
              <span class="match-count-row__num">{s.women || "-"}</span>
              {hasExceptional && (
                <span class="match-count-row__num">{s.exceptional || "-"}</span>
              )}
              <span class="match-count-row__rounds">
                {s.rounds.slice().sort((a, b) => a - b).join(" ")}
              </span>
            </div>
          ))}
        </div>
        <p class="muted small" style="margin:10px 0 0">
          유형 합계: 남복 {typeMatches.men} · 혼복 {typeMatches.mixed} · 여복 {typeMatches.women}
          {typeMatches.exceptional > 0 ? ` · 잡복 ${typeMatches.exceptional}` : ""}
        </p>
      </div>
    </div>
  );
}

export function buildMatchCountCheck(params: MatchCountCheckParams): HTMLElement | null {
  if (!params.matches || params.matches.length === 0) return null;
  return mountPreact(<MatchCountCheck {...params} />);
}
